/**
 * La fiche d'un élève ouverte pendant le live (« Son profil », `<TutorLiveStudentPanel>`) : l'élève de la séance,
 * complété par `getStudent`, et le brief de la séance (`getStudentBrief`, `context: in_live`).
 *
 * Le lot « Mes élèves » (#2932) a dessiné la fiche (`<TutorStudentProfilePage>`, ses formes dans
 * `ui/types/tutorEleves.ts`) mais ne l'a pas encore câblée : aucun adaptateur de la fiche n'existe à reprendre.
 * Celui-ci en écrit l'Aperçu, seul onglet que la fiche ouvre en live sans ses pages (Activités, Rapports,
 * Contrôles restent au lot qui les câblera), à partir des lectures de l'ancienne fiche en live
 * (`tutor/students/StudentProfil.js`, `StudentBriefSections.js`, `common/account/ObjectivesSummaryDisplay.js`).
 * Quand « Mes élèves » câblera la sienne, ce fichier lui cède la place.
 */
import moment from "moment"

import { type ApiSchoolSubject, toUISubject } from "../../parent/shared/schoolSubjectAdapter"
import {
    type UITutorSessionBrief,
    type UITutorStudentObjective,
    type UITutorStudentProfile,
} from "../../../ui/types/tutorEleves"
import { type UISubject } from "../../../ui/types/schoolSubject"
import { type ApiSessionObjective } from "./sessionObjectiveAdapter"
import { displayNameOf, type ApiLiveStudent } from "./tutorLiveStudentAdapter"

/** L'élève lu par `getStudent` : ce que la fiche ajoute à l'élève de la séance. */
export type ApiProfileStudent = ApiLiveStudent & {
    created_at?: string | null
    has_study_program?: boolean | null
    current_request_precisions?: string | null
    profile?: { class?: string | null; special_needs?: string[] | null } | null
    pedagogical_objectives?: (ApiSessionObjective | null)[] | null
}

/** Le brief (`GET api/students/{id}/brief`) : le profil et la séance, chacun un récit et ses données. */
export type ApiStudentBrief = {
    profile?: {
        content?: { profile_narrative?: string | null } | null
        structured_data?: { session_count?: number | null } | null
    } | null
    session?: {
        content?: { session_narrative?: string | null } | null
        structured_data?: {
            objective?: { subjects?: ({ name?: string | null } | null)[] | null } | null
            last_session?: {
                date?: string | null
                tutor_tags?: ({ type?: string | null; label?: string | null } | null)[] | null
                tutor_comment?: string | null
            } | null
            starting_block?: {
                chapitre?: string | null
                subject_name?: string | null
                assessment_declared?: boolean | null
                assessment_date?: string | null
                summary?: string | null
            } | null
        } | null
    } | null
}

/** Les libellés des besoins particuliers (`staticData.student_special_needs`, slug → libellé). */
export type SpecialNeedLabels = Record<string, string> | null | undefined

const subjectsOf = (ids: (number | string)[] | null | undefined, subjectsById: Record<string, ApiSchoolSubject>) =>
    (ids ?? [])
        .map((id) => subjectsById[String(id)])
        .filter((subject): subject is ApiSchoolSubject => subject != null)
        .map((subject) => toUISubject(subject))

/**
 * Le libellé déterministe d'un objectif (`ObjectivesSummaryDisplay.js:76-84`) : « Aide aux devoirs » ou
 * « Améliorer son niveau », en ses matières.
 */
export function objectiveTitle(objective: ApiSessionObjective, subjects: UISubject[]): string {
    const head = objective.type === "homework" ? "Aide aux devoirs" : "Améliorer son niveau"
    return subjects.length > 0 ? `${head} en ${subjects.map((subject) => subject.name).join(", ")}` : head
}

/** Ses objectifs non clos, l'objectif en attente dit « Demandé ». */
export function toProfileObjectives(
    student: ApiProfileStudent,
    subjectsById: Record<string, ApiSchoolSubject>
): UITutorStudentObjective[] {
    return (student.pedagogical_objectives ?? [])
        .filter((objective): objective is ApiSessionObjective => objective?.id != null && objective.status !== "closed")
        .map((objective) => {
            const subjects = subjectsOf(objective.subject_ids, subjectsById)
            const status = (objective.status ?? "").toLowerCase()
            return {
                id: objective.id!,
                title: objectiveTitle(objective, subjects),
                subjects,
                schedule: [],
                status: status === "pending" ? "requested" : status === "ongoing" ? "ongoing" : null,
            }
        })
}

/** Le brief de la séance (`BriefSessionSection`) : la dernière séance, ce que l'élève a dit au champion, le focus. */
export function toSessionBrief(
    brief: ApiStudentBrief | null | undefined,
    multiObjective: boolean
): UITutorSessionBrief | null {
    const data = brief?.session?.structured_data
    const focus = brief?.session?.content?.session_narrative ?? null
    if (data == null && focus == null) return null
    const subjects = data?.objective?.subjects ?? []
    const subjectLabel = subjects.length === 1 ? (subjects[0]?.name ?? null) : null
    const last = data?.last_session
    const starting = data?.starting_block
    return {
        nextSessionLabel: null,
        nextSessionSubjectLabel: subjectLabel,
        lastSession:
            last != null
                ? {
                      label: multiObjective && subjectLabel ? `Dernière séance en ${subjectLabel}` : "Dernière séance",
                      dateLabel: last.date ? moment(last.date).format("DD/MM/YYYY") : "",
                      tags: (last.tutor_tags ?? [])
                          .filter((tag): tag is { type?: string | null; label?: string | null } => tag?.label != null)
                          .slice(0, 3)
                          .map((tag) => ({
                              label: tag.label!,
                              tone: tag.type === "positive" ? "positive" : "negative",
                          })),
                      comment: last.tutor_comment ?? null,
                  }
                : null,
        assessments: [],
        startingBlock:
            starting != null
                ? {
                      wantsToWork:
                          starting.chapitre && starting.subject_name
                              ? `${starting.chapitre} en ${starting.subject_name}`
                              : null,
                      assessmentDeclared: starting.assessment_declared
                          ? `A déclaré un contrôle${starting.assessment_date ? ` le ${moment(starting.assessment_date).format("DD/MM")}` : ""}`
                          : null,
                      summary: starting.summary ?? null,
                  }
                : null,
        focus,
    }
}

/** La fiche, en Aperçu. `null` tant que l'élève n'est pas connu. */
export function toTutorLiveStudentProfile(
    student: ApiProfileStudent | null | undefined,
    brief: ApiStudentBrief | null | undefined,
    context: { subjectsById: Record<string, ApiSchoolSubject>; specialNeedLabels: SpecialNeedLabels }
): UITutorStudentProfile | null {
    if (student?.id == null) return null
    const labels = context.specialNeedLabels ?? {}
    const sessionCount = brief?.profile?.structured_data?.session_count ?? null
    const objectives = toProfileObjectives(student, context.subjectsById)
    const prioritized = (student.prioritized_school_subjects ?? []).map((subject) => subject?.id)
    return {
        student: {
            id: student.id,
            name: displayNameOf(student),
            avatarUrl: student.avatar ?? undefined,
            classLabel: student.profile?.class ?? null,
            subjects: subjectsOf(
                prioritized.filter((id): id is number | string => id != null),
                context.subjectsById
            ),
        },
        sessionCount,
        memberSinceLabel: student.created_at ? moment(student.created_at).format("MMM YYYY") : null,
        isNew: sessionCount != null && sessionCount <= 3,
        specialNeeds: (student.profile?.special_needs ?? [])
            .filter((slug) => labels[slug] != null)
            .map((slug) => labels[slug]!),
        objectives,
        requestPrecisions: student.current_request_precisions ?? null,
        hasStudyProgram: student.has_study_program === true,
        profileSummary: brief?.profile?.content?.profile_narrative ?? null,
        sessionBrief: toSessionBrief(brief, objectives.length > 1),
        game: {},
        details: [],
        // Jamais les coordonnées en live, même pour un admin (live du 04/11) : l'écran peut être partagé, et l'ancien
        // espace ne les montrait qu'au back-office, hors de la salle (`ProfilDetails.js`, `can("read", "backOffice")`).
        contacts: null,
    }
}
